import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { AppShell } from '../components/layout/AppShell';
import { PageBody } from '../components/layout/PageBody';
import { AlertCard } from '../components/alerts/AlertCard';
import { EmptyState } from '../components/common/EmptyState';
import { ErrorAlert } from '../components/common/ErrorAlert';
import { LoadingState } from '../components/common/LoadingState';
import { useFeedback } from '../components/common/FeedbackProvider';
import { useInfiniteAlerts } from '../hooks/useInfiniteAlerts';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useAuth } from '../hooks/useAuth';
import { ShellCard } from '../components/layout/ShellCard';
import { shellSx, tokens } from '../theme/tokens';
import { openDeepLink, tryPlayAlertMedia } from '../utils/alertOpen';
import { NOTIFICATION_KINDS } from '../utils/constants';
import { alertKindLabel } from '../utils/labels';
import { useDebouncedValue } from '../hooks/useDebouncedValue';

export function AlertsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSuccess, showError } = useFeedback();
  const [markingAll, setMarkingAll] = useState(false);
  const kind = searchParams.get('kind') || 'all';
  const qParam = searchParams.get('q') || '';
  const [searchInput, setSearchInput] = useState(qParam);
  const debouncedQ = useDebouncedValue(searchInput, 350);

  useEffect(() => {
    setSearchInput(qParam);
  }, [qParam]);

  useEffect(() => {
    const next = debouncedQ.trim();
    if (next === qParam) return;
    setSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        if (next) nextParams.set('q', next);
        else nextParams.delete('q');
        return nextParams;
      },
      { replace: true },
    );
  }, [debouncedQ, qParam, setSearchParams]);

  const alertFilters = useMemo(
    () => ({
      q: qParam || undefined,
      kind: kind !== 'all' ? kind : undefined,
    }),
    [qParam, kind],
  );

  const {
    items,
    unreadCount,
    loading,
    error,
    setError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    reload,
    open,
    invokeAction,
    markAllRead,
  } = useInfiniteAlerts(Boolean(user), 20, alertFilters);

  const sentinelRef = useInfiniteScroll({
    enabled: Boolean(user) && !loading && !error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const focusNid = searchParams.get('nid');
  const focusAction = searchParams.get('action');

  useEffect(() => {
    if (!focusNid || loading || items.length === 0) return undefined;
    const item = items.find((entry) => String(entry.id) === focusNid);
    if (!item) return undefined;

    const timer = window.setTimeout(() => {
      const card = document.querySelector(
        `[data-alert-id="${CSS.escape(focusNid)}"]`,
      );
      card?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      tryPlayAlertMedia(focusNid);

      if (focusAction && Array.isArray(item.actions)) {
        const action = item.actions.find((a) => a.id === focusAction);
        if (action) {
          void (async () => {
            try {
              await invokeAction(item, action);
              if (action.actionType === 'open_article') {
                openDeepLink(
                  { ...item, articleId: action.articleId || item.articleId },
                  navigate,
                  user,
                  showError,
                  { from: '/alerts' },
                );
              } else if (
                action.actionType === 'open_url' &&
                (action.url || item.url)
              ) {
                const href = action.url || item.url;
                openDeepLink(
                  {
                    ...item,
                    deepLink:
                      typeof href === 'string' &&
                      href.startsWith('/') &&
                      !href.startsWith('//')
                        ? href
                        : item.deepLink,
                    url: href,
                  },
                  navigate,
                  user,
                  showError,
                  { from: '/alerts' },
                );
              } else if (action.actionType !== 'dismiss') {
                showSuccess(action.label || 'Action completed');
              }
            } catch (e) {
              showError(e instanceof Error ? e.message : 'Action failed');
            }
          })();
        }
      }

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('nid');
          next.delete('action');
          return next;
        },
        { replace: true },
      );
    }, 120);

    return () => window.clearTimeout(timer);
  }, [
    focusNid,
    focusAction,
    loading,
    items,
    setSearchParams,
    invokeAction,
    navigate,
    user,
    showError,
    showSuccess,
  ]);

  async function onOpen(item) {
    try {
      await open(item);
      const opened = openDeepLink(item, navigate, user, showError, {
        from: '/alerts',
      });
      if (!opened && (item.kind === 'audio' || item.kind === 'video')) {
        showError(
          item.kind === 'audio'
            ? 'This alert has no audio yet.'
            : 'This alert has no video yet.',
        );
      } else if (
        !opened &&
        item.kind !== 'audio' &&
        item.kind !== 'video' &&
        item.kind !== 'banner'
      ) {
        showError('There’s nothing else to open.');
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to open alert';
      setError(message);
      showError(message);
    }
  }

  async function onAction(item, action) {
    try {
      await invokeAction(item, action);
      if (action.actionType === 'open_article') {
        openDeepLink(
          { ...item, articleId: action.articleId || item.articleId },
          navigate,
          user,
          showError,
          { from: '/alerts' },
        );
      } else if (action.actionType === 'open_url' && (action.url || item.url)) {
        const href = action.url || item.url;
        const isMediaFile =
          /\.(mp3|wav|ogg|m4a|aac|mp4|webm|mov|m4v|jpe?g|png|gif|webp)(\?|#|$)/i.test(
            href,
          );
        if (isMediaFile && item.id) {
          tryPlayAlertMedia(item.id);
        } else {
          // Reuse openDeepLink so stale /admin edit links resolve correctly
          // once the story is already published.
          const opened = openDeepLink(
            {
              ...item,
              deepLink:
                typeof href === 'string' && href.startsWith('/') && !href.startsWith('//')
                  ? href
                  : item.deepLink,
              url: href,
            },
            navigate,
            user,
            showError,
            { from: '/alerts' },
          );
          if (
            !opened &&
            typeof href === 'string' &&
            /^https?:\/\//i.test(href)
          ) {
            window.open(href, '_blank', 'noopener,noreferrer');
          }
        }
      } else {
        showSuccess(action.label || 'Action completed');
      }
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Action failed');
    }
  }

  async function onMarkAllRead() {
    if (!unreadCount || markingAll) return;
    setMarkingAll(true);
    try {
      const res = await markAllRead();
      const count = res?.markedCount ?? unreadCount;
      showSuccess(
        count > 0
          ? `Marked ${count} alert${count === 1 ? '' : 's'} as read`
          : 'All alerts already read',
      );
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Could not mark alerts as read');
    } finally {
      setMarkingAll(false);
    }
  }

  if (!user) {
    return (
      <AppShell title="Alerts" subtitle="Messages sent to you">
        <PageBody>
          <EmptyState
            title="Sign in to see your alerts"
            description="Sign in to get story alerts and messages from the newsroom."
            action={
              <Button
                component={RouterLink}
                to="/login"
                state={{ from: '/alerts' }}
                variant="contained"
                color="secondary"
                sx={{ color: '#FFFFFF' }}
              >
                Sign in
              </Button>
            }
          />
        </PageBody>
      </AppShell>
    );
  }

  return (
    <AppShell title="Your alerts" subtitle="Stories, reviews, and messages sent to you">
      <PageBody>
        <ErrorAlert message={error} onClose={() => setError(null)} />

        {error && !loading && (
          <Button variant="outlined" onClick={() => void reload()} sx={{ mb: 2 }}>
            Retry
          </Button>
        )}

        <ShellCard sx={{ mb: { xs: 1.5, sm: 2 } }}>
          <Stack spacing={1.25}>
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                gap: 1.25,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600, color: tokens.ink }}>
                {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
              </Typography>
              {unreadCount > 0 ? (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => void onMarkAllRead()}
                  disabled={markingAll}
                  sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
                >
                  {markingAll ? 'Marking…' : 'Mark all as read'}
                </Button>
              ) : null}
            </Box>
            <TextField
              size="small"
              label="Search alerts"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {NOTIFICATION_KINDS.map((k) => (
                <Chip
                  key={k}
                  clickable
                  size="small"
                  label={k === 'all' ? 'All types' : alertKindLabel(k)}
                  color={kind === k ? 'secondary' : 'default'}
                  variant={kind === k ? 'filled' : 'outlined'}
                  onClick={() =>
                    setSearchParams(
                      (prev) => {
                        const next = new URLSearchParams(prev);
                        if (k === 'all') next.delete('kind');
                        else next.set('kind', k);
                        return next;
                      },
                      { replace: true },
                    )
                  }
                />
              ))}
            </Stack>
          </Stack>
        </ShellCard>

        {loading && <LoadingState label="Loading alerts…" />}
        {!loading && items.length === 0 && !error && (
          <EmptyState
            title={
              qParam || kind !== 'all'
                ? 'No alerts match this filter'
                : 'No alerts yet'
            }
            description={
              qParam || kind !== 'all'
                ? 'Try All types, or clear the search.'
                : 'New staff alerts will show up here.'
            }
            action={
              qParam || kind !== 'all' ? (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSearchInput('');
                    setSearchParams({}, { replace: true });
                  }}
                >
                  Show all
                </Button>
              ) : null
            }
          />
        )}
        {items.length > 0 && (
          <Stack spacing={{ xs: 1.5, sm: 2 }}>
            {items.map((item, index) => (
              <AlertCard
                key={item.id}
                item={item}
                index={index}
                onOpen={onOpen}
                onAction={onAction}
              />
            ))}
          </Stack>
        )}

        {!loading && !error && items.length > 0 && (
          <Box sx={{ py: { xs: 2, sm: 3 }, textAlign: 'center' }}>
            {isFetchingNextPage && (
              <Typography variant="body2" sx={shellSx.textMuted}>
                Loading more…
              </Typography>
            )}
            {!hasNextPage && !isFetchingNextPage && (
              <Typography variant="body2" sx={shellSx.textMuted}>
                You&apos;re all caught up
              </Typography>
            )}
          </Box>
        )}

        <Box ref={sentinelRef} sx={{ height: 1 }} aria-hidden />
      </PageBody>
    </AppShell>
  );
}
